import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { QuoteVersions } from '../model/quote/quote_versions.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { QuoteService } from '../quote/quote.service';
import { GridOptions } from "ag-grid-community";
import { QuoteNotes } from '../model/quote/quote_notes.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { formatDate } from '@angular/common';
import { LocalStorageService } from 'ngx-webstorage';
import { SharedService } from '../services/shared.service';
import { EquineService } from '../quote/equine/equine.service';

@Component({
  selector: 'app-quote-notes',
  templateUrl: './quote-notes.component.html',
  styleUrls: ['./quote-notes.component.css']
})
export class QuoteNotesComponent implements OnInit {
  @Input() quote: QuoteVersions;
  @Input() isEquine: boolean = false;
  @Output() hasImportantNotes = new EventEmitter();
  notes = new QuoteNotes();
  referralReasons: any[] = [];

  private lastFetchedQuoteId: string = '';

  constructor(
    private quoteService: QuoteService, 
    private modalService: NgbModal, 
    private sharedService: SharedService,
    private sessionSt: LocalStorageService, 
    private _snackBar: MatSnackBar,
    private equineService: EquineService
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['quote'] && this.quote?.id) {
      if (this.quote.id !== this.lastFetchedQuoteId) {
        this.fetchNotes();
      }
      if (this.isEquine) {
        this.getReferralReasons();
      }
    }
  }

  get showReferralReasons(): boolean {
    return this.isEquine && this.referralReasons.length > 0;
  }

  isInsurer: boolean = false;
  ngOnInit(): void {
    if (this.sessionSt.retrieve('is_insurer')) {
      this.isInsurer = true;
    }
    if (this.quote?.id) {
      this.fetchNotes();
      if (this.isEquine) {
        this.getReferralReasons();
      }
    }
  }

  public getReferralReasons(): void {
    this.equineService
      .getQuoteReferralReasons(this.quote?.id)
      .subscribe({
        next: (response: any) => {
          const concatenated = (response?.result?.concatenated_reasons || '').toString().trim();
          if (concatenated) {
            this.referralReasons = [{
              reason: concatenated,
              latest_date: response.result.latest_date ?? null
            }];
            return;
          }
          this.syncReferralReasonsFromQuote();
        },
        error: () => {
          this.syncReferralReasonsFromQuote();
        }
      });
  }

  private syncReferralReasonsFromQuote(): void {
    const sofReasons = Array.isArray(this.quote?.referred_reasons) ? this.quote.referred_reasons : [];
    if (sofReasons.length) {
      this.referralReasons = sofReasons.map((item: any) => ({
        reason: [item?.question, item?.response_text, item?.trigger]
          .map((part) => (part || '').trim())
          .filter(Boolean)
          .join(' — ') || 'Referral',
        latest_date: this.quote?.updated_at ?? null,
      }));
      return;
    }
    const manual = (this.quote?.manually_referred_reason || '').trim();
    if (manual) {
      this.referralReasons = [{
        reason: manual,
        latest_date: this.quote?.updated_at ?? null,
      }];
      return;
    }
    this.referralReasons = [];
  }

  fetchNotes() {
    if (!this.quote?.id) return;
    this.lastFetchedQuoteId = this.quote.id;
    const isAdmin = this.sessionSt.retrieve('company_role') == 'Peliwica'
      && this.sessionSt.retrieve('user_group') == 'Administrator';
    this.quoteService.getNotes(this.quote.id)?.subscribe(
      (response: any) => {
        if (response.success) {
          const notesList = response.result ?? [];
          notesList.forEach((res: any) => { res.isAdmin = isAdmin; });
          this.quote.notes = notesList;
          this.rowData = notesList;
          this.isImportantNote();
        }
      },
      () => {
        this._snackBar.open('Failed to load notes.', 'Close', { duration: 3000, panelClass: 'text-danger' });
      }
    );
  }

  gridOptions: GridOptions = {
    onGridReady: () => {
      this.gridOptions.api.sizeColumnsToFit();
    }
  };
  columnDefs = [
    {
      headerName: 'Notes', field: 'notes', sortable: true, width: 500,
      cellRenderer: function (params) {
        return ((params.data.is_important == 1) ? '<label class="badge badge-danger">Important</label> ' : '') + '<a data-action-type="view">' + params.data.notes + '</a>';
      },
      tooltip: function (params) {
        return params.data.notes;
      },
    },
    {
      headerName: 'Created By', field: 'first_name', sortable: true, width: 120,
      cellRenderer: function (params) {
        if (params.data.user) {
          return params.data.user.first_name + ' ' + params.data.user.last_name;
        }
      }
    },
    {
      headerName: 'Created At', field: 'created_at', sortable: true, width: 200,
      comparator: this.sharedService.agGridDteSortingComparator,
      cellRenderer: function (params) {
        return formatDate(params.data.created_at, 'dd-MM-yyyy hh:mm a', 'en-US');
      }
    },
    {
      headerName: null,
      width: 120,
      cellRenderer: function (params) {
        let links = '';
        if (params.data.isAdmin) {
          links += '<div class="text-right"><a title="Edit Notes" style="font-size: 18px;"><i class="mdi mdi-grease-pencil" data-action-type="edit" id="' + params.data.id + '"></i></a></div>';
        }
        return links;
      }
    }
  ];
  rowData: any = [];
  referralReasonNotes = [
    {
      headerName: 'Referral Reason', field: 'reason', sortable: true, width: 500,
      cellRenderer: function (params) {
        return `<a data-action-type="view">${params.data.reason || ''}</a>`;
      }
    },
    {
      headerName: 'Created At', field: 'latest_date', sortable: true, width: 200,
      comparator: this.sharedService.agGridDteSortingComparator,
      cellRenderer: function (params) {
        return formatDate(params.data.latest_date, 'dd-MM-yyyy hh:mm a', 'en-US');
      }
    },
  ];

  notesText;
  onReferralGridRowClicked(e: any, notesViewModel): void {
    const actionType = e?.event?.target?.getAttribute('data-action-type');
    if (actionType !== 'view') return;
    this.notesText = e?.data?.reason || '';
    this.modalService.open(notesViewModel, { centered: true });
  }

  onGridRowClicked(e: any, content, deleteModel, notesViewModel) {
    if (e) {
      let actionType = e.event.target.getAttribute("data-action-type");
      switch (actionType) {
        case "edit":
          {
            this.modalService.open(content, { centered: true });
            this.notes = e.data;
            break;
          }
        case "view":
          {
            this.modalService.open(notesViewModel, { centered: true });
            this.notesText = e.data.notes;
            break;
          }
        case "delete": {
          this.modalService.open(deleteModel, { centered: true });
          this.notes = e.data;
        }
      }
    }
  }

  deleteNotes(id) {
    this.quoteService.deleteNotes(id)
      .subscribe(
        (response: any) => {
          this.modalService.dismissAll();
          if (response.success) {
            this.fetchNotes();
          } else {
            this._snackBar.open(response.message || 'Failed to delete note.', 'Close', {
              duration: 3000,
              panelClass: 'text-danger'
            });
          }
        },
        () => {
          this.modalService.dismissAll();
          this._snackBar.open('Failed to delete note.', 'Close', { duration: 3000, panelClass: 'text-danger' });
        }
      );
  }

  /*
  * ADD NOTES
  */
  openNotesPopup(content) {
    this.modalService.open(content, { centered: true });
    this.notes = new QuoteNotes();
  }

  saveNotes() {
    let data = {
      id: this.notes.id,
      notes: this.notes.notes,
      quote_id: this.quote.id,
      is_important: this.notes.is_important
    };
    this.quoteService.saveNotes(data)
      .subscribe(
        (response: any) => {
          this.modalService.dismissAll();
          if (response.success) {
            this.fetchNotes();
          } else {
            this._snackBar.open(response.message, '', {
              duration: 5000,
              panelClass: 'text-danger'
            });
          }
        },
        () => {
          this.modalService.dismissAll();
          this._snackBar.open('Failed to save note.', 'Close', { duration: 3000, panelClass: 'text-danger' });
        }
      );
  }

  isImportantNote() {
    let is_important = false;
    this.rowData.forEach((res) => {
      if (res.is_important == 1) {
        is_important = true;
      }
    })
    this.hasImportantNotes.emit(is_important);
  }
}
